// @ts-nocheck
import { hasAnalyticsConsent, analyticsSurfaceAllowed } from './analytics-consent.ts';
import { analyticsContext, safeAnalyticsUrl, safeEventParameters } from '../../shared/web-analytics.ts';
const CONSENT_KEY = 'ac_cookie_consent';

function hasConsent() {
  return hasAnalyticsConsent();
}

function browserFamily(userAgent = '') {
  if (/Edg\//.test(userAgent)) return 'Edge';
  if (/OPR\//.test(userAgent)) return 'Opera';
  if (/Firefox\//.test(userAgent)) return 'Firefox';
  if (/Chrome\//.test(userAgent)) return 'Chrome';
  if (/Safari\//.test(userAgent)) return 'Safari';
  return 'Other';
}

function osFamily(userAgent = '') {
  if (/Windows/.test(userAgent)) return 'Windows';
  if (/Android/.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS';
  if (/Mac OS X/.test(userAgent)) return 'macOS';
  if (/Linux/.test(userAgent)) return 'Linux';
  return 'Other';
}

function deviceClass(userAgent = '') {
  if (/iPad|Tablet/i.test(userAgent)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/i.test(userAgent)) return 'mobile';
  return 'desktop';
}

function analyticsSessionId() {
  if (!hasConsent()) return '';
  const key = 'ac_analytics_session';
  try {
    const current = window.sessionStorage.getItem(key);
    if (current) return current;
    const value = globalThis.crypto?.randomUUID?.()
      || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
    window.sessionStorage.setItem(key, value);
    return value;
  } catch {
    return '';
  }
}

function referrerHost() {
  try {
    return document.referrer ? new URL(document.referrer).hostname : '';
  } catch {
    return '';
  }
}

function browserMetadata() {
  if (!hasConsent() || typeof navigator === 'undefined') return {};
  const language = String(navigator.language || '').slice(0, 35);
  const localeRegion = language.match(/[-_]([A-Za-z]{2})\b/)?.[1]?.toUpperCase() || '';
  const userAgent = String(navigator.userAgent || '');
  const query = new URLSearchParams(window.location.search);
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    timezone = '';
  }
  return {
    sessionId: analyticsSessionId(),
    browserLanguage: language,
    siteLocale: String(document.documentElement.lang || '').slice(0, 20),
    localeRegion,
    timezone: String(timezone).slice(0, 80),
    device: deviceClass(userAgent),
    browser: browserFamily(userAgent),
    os: osFamily(userAgent),
    screen: `${Number(window.screen?.width || 0)}x${Number(window.screen?.height || 0)}`,
    referrerHost: referrerHost().slice(0, 120),
    utmSource: String(query.get('utm_source') || '').slice(0, 100),
    utmMedium: String(query.get('utm_medium') || '').slice(0, 100),
    utmCampaign: String(query.get('utm_campaign') || '').slice(0, 140),
  };
}

export function initAnalytics() {
  if (typeof window === 'undefined') return false;
  if (!hasConsent() || !analyticsSurfaceAllowed()) return false;
  if (typeof window.__configureAnalytics === 'function') {
    window.__configureAnalytics();
  }
  return Boolean(window.__acAnalyticsConfigured);
}

// Events emitted before the consent-gated gtag script finishes loading used to be
// dropped, so the first event of a visit (the landing page_view) never reached GA4
// while the first-party record kept it. Queue them and flush once gtag is configured.
const GA_QUEUE_LIMIT = 50;
const gaEventQueue = [];

export function flushGaQueue() {
  if (typeof window === 'undefined') return;
  if (!hasConsent() || !analyticsSurfaceAllowed() || !window.__acAnalyticsConfigured || typeof window.gtag !== 'function') return;
  while (gaEventQueue.length) {
    const [name, params] = gaEventQueue.shift();
    window.gtag('event', name, { ...params, send_to: window.AC_GA_ID });
  }
}

function sendToGa(name, params) {
  if (typeof window.gtag === 'function' && window.__acAnalyticsConfigured) {
    flushGaQueue();
    window.gtag('event', name, { ...params, send_to: window.AC_GA_ID });
    return;
  }
  if (!window.AC_ANALYTICS_ENABLED) return;
  gaEventQueue.push([name, params]);
  if (gaEventQueue.length > GA_QUEUE_LIMIT) gaEventQueue.shift();
}

// Hand the flusher to the inline loader (index.html / article.html), which calls it
// right after gtag('config'); also start loading the tag at module import when the
// consent choice already exists, so the first tracked event races less with the load.
if (typeof window !== 'undefined') {
  window.__acFlushAnalyticsQueue = flushGaQueue;
  try {
    if (window.localStorage.getItem(CONSENT_KEY) === 'accepted') initAnalytics();
  } catch {
    // Storage unavailable; analytics stays consent-gated and inert.
  }
}

function emit(name, params = {}) {
  if (typeof window === 'undefined') return;
  if (!hasConsent()) return;

  const safe = { ...analyticsContext('artificial_careers', window.location.pathname, '', document.documentElement.lang || 'en'), ...safeEventParameters(params) };
  if (params.page_path) safe.page_path = params.page_path;
  emitProductEvent(name, safe);
  if (!analyticsSurfaceAllowed()) return;
  const location = safeAnalyticsUrl(window.location.href, 'https://artificial.careers');
  if (!location) return;
  safe.page_location = location;
  if (referrerHost() === 'accounts.google.com') safe.ignore_referrer = true;
  initAnalytics();
  sendToGa(name, safe);

  if (typeof window.plausible === 'function') {
    window.plausible(name, { props: safe });
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: 'ac_analytics_event', event_name: name, ...safe });
  }
}

export function disableAnalytics() {
  if (typeof window === 'undefined') return;
  if (window.AC_GA_ID) window['ga-disable-' + window.AC_GA_ID] = true;
  if (typeof window.gtag === 'function') window.gtag('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
  window.__acAnalyticsConfigured = false;
  window.__acAnalyticsLoading = false;
  gaEventQueue.length = 0;
  lastPageView = '';
  document.querySelectorAll('script[data-ac-analytics="google"]').forEach((script) => script.remove());
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0].trim();
    if (!/^_ga(?:_|$)/.test(name)) continue;
    for (const domain of ['', window.location.hostname, '.' + window.location.hostname, '.artificial.careers']) document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? '; Domain=' + domain : ''}`;
  }
  try {
    window.sessionStorage.removeItem('ac_analytics_session');
  } catch {
    // Storage may be unavailable in privacy-restricted browsers.
  }
}

function emitProductEvent(name, params = {}) {
  const payload = {
    eventName: name,
    surface: String(params.page_path || window.location.pathname || '').slice(0, 120),
    resourceType: params.job_id ? 'job' : params.slug ? 'content' : '',
    resourceId: String(params.job_id || params.slug || '').slice(0, 180),
    outcome: String(params.outcome || '').slice(0, 80),
    metadata: browserMetadata(),
  };
  fetch('/api/product-events', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

let lastPageView = '';
export function trackPageView(path) {
  if (!hasConsent()) return;
  const key = `${analyticsSessionId()}:${path}`;
  if (lastPageView === key) return;
  lastPageView = key;
  emit('page_view', {
    page_path: path,
    page_location: typeof window !== 'undefined' ? window.location.href : path,
    page_title: typeof document !== 'undefined' ? document.title : path
  });
  const article = path.match(/^\/(?:learn-ai\/article|learn-ai|blog)\/([^/]+)\/?$/);
  const event = path.match(/^\/events?\/([^/]+)\/?$/);
  const job = path.match(/^\/job\/([^/]+)\/?$/);
  if (article) emit('article_view', { slug: article[1] });
  if (event) emit('event_view', { slug: event[1] });
  if (job) emit('job_view', { job_id: job[1] });
}

export function trackSearch(params = {}) {
  emit('search', { ...params, has_query: Boolean(params.query), query_length: String(params.query || '').length, filter_count: Object.values(params).filter(Boolean).length });
}

export function trackJobClick(job = {}, interaction = 'detail') {
  emit(interaction === 'apply' ? 'job_apply_click' : 'job_click', {
    job_id: String(job.id || ''),
    company: job.company || '',
    title: job.title || '',
    interaction_source: interaction === 'apply' ? 'apply_link' : 'job_detail'
  });
}

export function trackCompanyClick(company = {}) {
  emit('company_click', {
    company: company.name || company.company || '',
    slug: company.slug || company.companySlug || ''
  });
}

export function trackArticleClick(article = {}) {
  emit('article_click', {
    slug: article.slug || '',
    title: article.title || ''
  });
}

export function trackJobView(job = {}) { emit('job_view', { job_id: String(job.id || '') }); }
